<template>
  <Layout>
    <!-- Header -->
    <div class="row">
      <PageHeader class="col-9" title="Automatic Traffic Counting and Classification" />
    </div>

    <!-- Tiles and donut chart -->
    <div class="row mt-2">
      <!-- violation details -->
      <div class="col-xl-6">
        <div class="card">
          <div class="card-body">
            <b-card-title>
              <h4 class="card-title">
                {{ $t("menuitems.incident.list.dashboard.list.violations") }}
              </h4>
            </b-card-title>
            <hr class="mb-4" />
            <div class="row">
              <div class="col-6 mb-3">
                <div class="card mini-stats-wid border border-info">
                  <div class="card-body">
                    <div class="d-flex flex-wrap">
                      <div class="me-3">
                        <p class="text-muted mb-4">
                          <i class="bx bxs-error" /> Total Traffic Count
                        </p>
                        <h2 class="mb-0" v-if="atccStats">
                          {{ atccStats.totalTrafficCount }}
                        </h2>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-6">
                <div class="card mini-stats-wid border border-warning">
                  <div class="card-body">
                    <div class="d-flex flex-wrap">
                      <div class="me-3">
                        <p class="text-muted mb-4">
                          <i class="bx bxs-error" /> Today's Traffic Count
                        </p>
                        <h2 class="mb-0" v-if="atccStats">
                          {{ atccStats.todayTrafficCount }}
                        </h2>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-6">
                <div class="card mini-stats-wid border border-danger">
                  <div class="card-body">
                    <div class="d-flex flex-wrap">
                      <div class="me-3">
                        <p class="text-muted mb-4">
                          <i class="bx bxs-error" /> This Month
                        </p>
                        <h2 class="mb-0" v-if="atccStats">
                          {{
                            atccStats.monthTrafficCount
                          }}
                        </h2>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-6">
                <div class="card mini-stats-wid border border-success">
                  <div class="card-body">
                    <div class="d-flex flex-wrap">
                      <div class="me-3">
                        <p class="text-muted mb-4">
                          <i class="bx bxs-error" /> This Year
                        </p>
                        <h2 class="mb-0" v-if="atccStats">
                          {{ atccStats.yearTrafficCount }}
                        </h2>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- donut chart for violations -->
      <div class="col-xl-6">
        <div class="card">
          <div class="card-body">
            <div class="d-flex flex-wrap">
              <div class="row me-2">
                <b-card-title>
                  <h4 class="card-title">Vehicle Classwise Statistics</h4>
                </b-card-title>
                <hr />
                <div class="card-body m-0 p-0">
                  <apexchart class="apex-charts" height="308" type="bar" dir="ltr" :series="classwiseCount.series"
                    :options="classwiseCount.chartOptions"></apexchart>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <!-- Class wise speeding -->
      <div class="col-6">
        <div class="card">
          <div class="card-body">
            <div class="d-flex flex-wrap">
              <div class="row me-2">
                <b-card-title>
                  <h4 class="card-title">Average Vehicle Class Speed </h4>
                </b-card-title>
                <hr />
                <div class="card-body m-0 p-0">
                  <apexchart class="apex-charts" height="430" type="bar" dir="ltr" :series="speedChartData.series"
                    :options="speedChartData.chartOptions"></apexchart>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Last 10 transactions -->
      <div class="col-6">
        <div class="card">
          <div class="card-body">
            <b-card-title>
              <h4 class="card-title">Latest 20 Vehicles</h4>
            </b-card-title>
            <hr />
            <Loader>
              <simplebar style="max-height: 51vh;">
                <div class="table-responsive mb-0">
                  <b-table class="datatables" :items="trafficData" :fields="fields" responsive="sm" :per-page="0"
                    :current-page="currentPage" :sort-by.sync="sortBy" :sort-desc.sync="sortDesc">
                    <template #cell(plate_no)="row">
                      {{ !row.item.plate_no ? "NA" : row.item.plate_no }}
                    </template>

                    <template #cell(vehicle_speed)="row">
                      {{ !row.item.vehicle_speed ? "NA" : `${row.item.vehicle_speed} km/hr` }}
                    </template>

                    <template #cell(veh_image_url)="row">
                      <img v-if="row.item.veh_image_url !== 'NA'" :src="row.item.veh_image_url" width="80" @click="
                        showImage(row.item.veh_image_url)
                        " style="cursor: pointer" />
                      <span v-else>Not Available</span>
                    </template>

                    <!-- <template #cell(plate_image_url)="row">
                      <img v-if="row.item.plate_image_url !== 'NA'" :src="row.item.plate_image_url" width="80" @click="
                        showImage(row.item.plate_image_url)
                        " style="cursor: pointer" />
                      <span v-else>Not Available</span>
                    </template> -->
                  </b-table>
                </div>
              </simplebar>
            </Loader>
          </div>
        </div>
      </div>
    </div>

    <!-- Incident details modal -->
    <b-modal id="more-details-modal" size="xl" title-class="font-18" hide-footer centered>
      <template #modal-header>
        <div>
          <h5>Incident Details</h5>
        </div>
        <div>
          <button class="btn btn-sm btn-outline-danger m-2" @click="$bvModal.hide('more-details-modal')">
            <i class="mdi mdi-close"></i> Close
          </button>
        </div>
      </template>

      <div class="row">
        <div class="col-xl-6">
          <div class="card">
            <div class="card-body">
              <div class="table-responsive">
                <table class="table mb-0">
                  <tbody>
                    <tr>
                      <th scope="row">Id</th>
                      <td>{{ selectedIncident.id }}</td>
                    </tr>
                    <tr>
                      <th scope="row">Vehicle Number</th>
                      <td>
                        {{
                          selectedIncident.plate_no != null
                          ? selectedIncident.plate_no
                          : "NA"
                        }}
                      </td>
                    </tr>
                    <tr>
                      <th scope="row">Vehicle Class</th>
                      <td>
                        {{
                          selectedIncident.class_name != null
                          ? selectedIncident.class_name
                          : "NA"
                        }}
                      </td>
                    </tr>
                    <tr>
                      <th scope="row">Speed</th>
                      <td>
                        {{
                          selectedIncident.speed != null
                          ? selectedIncident.speed
                          : "NA"
                        }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <div class="col-xl-6">
          <div class="card">
            <div class="card-body">
              <div class="table-responsive">
                <table class="table mb-0">
                  <tbody>
                    <tr>
                      <th scope="row">Speed</th>
                      <td>
                        {{
                          selectedIncident.speed != null
                          ? selectedIncident.speed
                          : "NA"
                        }}
                      </td>
                    </tr>
                    <tr>
                      <th scope="row">Speed Limit</th>
                      <td>
                        {{
                          selectedIncident.speed_limit != null
                          ? selectedIncident.speed_limit
                          : "NA"
                        }}
                      </td>
                    </tr>
                    <tr>
                      <th scope="row">Date/Time</th>
                      <td>
                        {{
                          getReadableDateTime(
                            selectedIncident.incident_datetime
                          )
                        }}
                      </td>
                    </tr>
                    <tr>
                      <th scope="row">Source</th>
                      <td>{{ selectedIncident.source_name }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-12">
          <div class="card">
            <div class="card-body">
              <h4 class="card-title">Incident Photos</h4>
              <img v-for="(src, index) in incidentImages" :key="index" class="float-left image"
                @click="() => showCaptiomImg(index)" width="150" :src="src" />
            </div>
            <vue-easy-lightbox :visible="visibleCaption" :imgs="incidentImages" :index="index"
              @hide="handleCaptionHide"></vue-easy-lightbox>
          </div>
        </div>
      </div>
    </b-modal>

    <!-- Quick image preview -->
    <vue-easy-lightbox :visible="showSingleImage" :imgs="selectedImage" :index="index"
      @hide="handleCaptionHide"></vue-easy-lightbox>
  </Layout>
</template>

<script>
import Layout from "@/layouts/main";
import appConfig from "@/app.config";
import PageHeader from "@/components/page-header";
import DatePicker from "vue2-datepicker";
import VueEasyLightbox from "vue-easy-lightbox";
import rpoMap from "../../views/incident/rpoVehicleMap.vue";
import atccMixin from "../../mixins/atcc";
import { mapActions } from "vuex";
import VueSlideBar from "vue-slide-bar";
import Loader from "../../components/widgets/loader.vue";
import "../../assets/plugins/range-slider/vue-range-slider.css";
import VueRangeSlider from "../../assets/plugins/range-slider/vue-range-slider";
import DonutChart from "../../views/incident/atccDonutChart.vue";
import simplebar from "simplebar-vue";
import VehicleWiseData from "./vehicleWiseData.vue"
import config from '../../constants/config'


/**
 * Dashboard Component
 */
export default {
  page: {
    title: "Incident Management",
    meta: [
      {
        name: "description",
        content: appConfig.description,
      },
    ],
  },

  props: {
    updating: {
      type: Boolean,
    },
  },

  mixins: [atccMixin],

  components: {
    Layout,
    PageHeader,
    VueEasyLightbox,
    rpoMap,
    DatePicker,
    VueSlideBar,
    Loader,
    VueRangeSlider,
    DonutChart,
    simplebar,
    VehicleWiseData
  },

  data () {
    return {
      isDataFilterActive: false,
      speedFilter: [0, 500],
      currentChallanModalId: null,
      filterVehicleNumber: null,
      filterVehicleSpeed: 0,
      filterVehicleClass: null,
      eventCategory: null,
      datepickervalueFrom: null,
      datepickervalueTo: null,
      subCategoryId: null,
      vehicleNumber: null,
      vehicleClass: null,
      levelOfAccident: null,
      incidentId: null,
      visibleCaption: false,
      selectedIncident: {},
      resolveComment: "",
      index: 1,
      totalRows: 1,
      currentPage: 1,
      perPage: 1,
      sortBy: "id",
      sortDesc: true,
      filterOn: [],
      filter: null,
      loaderValue: false,
      selectedImage: null,
      showSingleImage: false,
      fields: [
        {
          key: "plate_no",
          label: "Vehicle No.",
          sortable: false,
        },
        {
          key: "vehicle_speed",
          label: "Speed",
          sortable: false
        },
        {
          key: "veh_image_url",
          label: "Image",
          sortable: false,
          formatter: (value) => {
            return `${config.CLIENT_URL}${value}`
          },
        },
        {
          key: "created_at",
          label: "Timestamp",
          thStyle: { width: "10%" },
          sortable: true,
          formatter: (value) => {
            return this.getReadableDateTime(value);
          },
        }
      ],
      socketInterval: null,
    };
  },

  methods: {
    ...mapActions("incident", ["updateIncidents"]),
    showCaptiomImg (index) {
      this.index = index;
      this.visibleCaption = true;
    },

    handleCaptionHide () {
      this.visibleCaption = false;
      this.showSingleImage = false;
    },

    showImage (imageUrl) {
      this.selectedImage = [imageUrl];
      this.showSingleImage = true;
    },
    getStatusClass (status) {
      switch (status) {
        case "Open":
          return "bg-danger";

        case "In Progress":
          return "bg-warning";

        case "Resolved":
          return "bg-success";

        case "Closed":
          return "bg-success";

        default:
          return "bg-info";
      }
    },

    getReadableDateTime (dateTime) {
      if (dateTime) {
        // const date = String(dateTime).split("T");
        return `${new Date(dateTime).toLocaleString()}`;
      }
    },

    showIncidentDetails (incident) {
      this.selectedIncident = incident;
      this.vehicleNumber = this.selectedIncident.plate_no;
      this.vehicleClass = this.selectedIncident.vehicle_class_id;
      this.$bvModal.show("more-details-modal");
    },
  },

  computed: {
    atccStats () {
      return this.$store.getters["socket/atcc"].statistics;
    },
    trafficData () {
      return this.$store.getters["socket/atcc"].data;
    },
    incidentImages () {
      return this.selectedIncident.images
        ? this.selectedIncident.images.map((e) => e.media_url)
        : [];
    },
    currentUser () {
      return this.$store.getters["auth/user"];
    },
    classwiseCount () {
      return {
        series: [{
          data: (this.atccStats.classwiseCount) ? this.atccStats.classwiseCount.map((e) => e.data) : []
        }],
        chartOptions: {
          chart: {
            toolbar: {
              show: false,
            }
          },
          plotOptions: {
            bar: {
              horizontal: true,
            }
          },
          colors: ['#3d85c6'],
          grid: {
            borderColor: '#fff2cc',
          },
          xaxis: {
            categories: (this.atccStats.classwiseCount) ? this.atccStats.classwiseCount.map((e) => e.category) : [],
          }
        }
      }
    },
    speedChartData () {
      return {
        series: [{
          data: (this.atccStats.averageSpeedCount) ? this.atccStats.averageSpeedCount.map((e) => e.data) : []
        }],
        chartOptions: {
          chart: {
            toolbar: {
              show: true,
            }
          },
          plotOptions: {
            bar: {
              horizontal: false,
            }
          },
          colors: ['#EAAB00', '#fff2cc', '#214597'],
          grid: {
            borderColor: '#fff2cc',
          },
          xaxis: {
            categories: (this.atccStats.averageSpeedCount) ? this.atccStats.averageSpeedCount.map((e) => e.category) : [],
          }
        }
      }
    }
  },

  async created () {
    this.socketInterval = setInterval(async () => {
      if (!this.isDataFilterActive) {
        this.$socket.emit("REQUEST:ATCC_STATISTICS");
        this.$socket.emit("REQUEST:ATCC_TRAFFIC_DATA");
      }
    }, 10000);

    this.$socket.emit("REQUEST:ATCC_STATISTICS");
    this.$socket.emit("REQUEST:ATCC_TRAFFIC_DATA");
  },

  beforeDestroy () {
    if (this.socketInterval) {
      clearInterval(this.socketInterval);
      this.socketInterval = null;
    }
  },
};
</script>

<style scoped>
.image {
  height: 100px;
  cursor: pointer;
  margin: 5px;
  display: inline-block;
}

/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.apexcharts-tooltip span {
  color: #ffffff;
}
</style>
